import { ProviderPayerSubscriber, ProviderSubscriberPatient } from '@enter/core';
import _ from 'lodash';

const ORDINALS = ['Primary', 'Secondary', 'Tertiary'];

/**
 * Label for one payerSubscribers entry, e.g. "Secondary — Aetna - W123". Pass no `index` when the entry is
 * not part of an ordered list (a single transmitted payer), so no coverage tier is implied.
 */
export function getPayerSubscriberLabel(payerSubscriber: ProviderPayerSubscriber, index?: number): string {
    const subscriber = payerSubscriber.subscriber;
    const detail = payerSubscriber.payer
        ? `${payerSubscriber.payer.name} - ${payerSubscriber.memberId}`
        : [subscriber?.first, subscriber?.last].filter(Boolean).join(' ');
    const ordinal = index === undefined ? undefined : ORDINALS[index] ?? `#${index + 1}`;

    return [ordinal, detail || `Payer subscriber ${payerSubscriber.id ?? ''}`.trim()].filter(Boolean).join(' — ');
}

/**
 * Label for one subscriberPatients entry, e.g. "Jane Doe — subscriber John Doe (Spouse)".
 * These entries are relationships, not coverage tiers, so the label names the subscriber and the
 * relationship rather than an array position: two entries can share a patient and differ only by subscriber.
 */
export function getSubscriberPatientLabel(subscriberPatient: ProviderSubscriberPatient): string {
    const { patient, subscriber, relationship } = subscriberPatient;
    const patientName = [patient?.first, patient?.last].filter(Boolean).join(' ');
    const subscriberName = [subscriber?.first, subscriber?.last].filter(Boolean).join(' ');
    const relation = relationship ? _.capitalize(_.lowerCase(String(relationship))) : '';
    const subscriberPart = subscriberName ? `subscriber ${subscriberName}${relation ? ` (${relation})` : ''}` : relation;

    return [patientName, subscriberPart].filter(Boolean).join(' — ') || 'Unnamed relationship';
}

/**
 * The subscriber-patient relationship the CMS-1500 for `payerSubscriber` is built from: the one that
 * belongs to the SAME subscriber. With a single payer subscriber there is nothing to disambiguate, so
 * the claim's first relationship is used. Several payers and no match is unknown, never a guess.
 */
export function findSubscriberPatientFor(
    payerSubscriber: ProviderPayerSubscriber | undefined,
    payerSubscribers: ProviderPayerSubscriber[],
    subscriberPatients: ProviderSubscriberPatient[],
): ProviderSubscriberPatient | undefined {
    const subscriberId = payerSubscriber?.subscriber?.id;
    const matching = subscriberId ? subscriberPatients.filter(p => p.subscriber?.id === subscriberId) : [];

    if (matching.length) {
        return matching[0];
    }

    return payerSubscribers.length === 1 ? subscriberPatients[0] : undefined;
}
